import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { uploadFile } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { Camera, Mail, Edit2, Settings as SettingsIcon, Crown, BadgeCheck, Loader2, Check, X } from "lucide-react";

function deriveStatus(lastSeenAt) {
  if (!lastSeenAt) return { key: "offline", label: "Offline", color: "bg-zinc-400" };
  const mins = Math.floor((Date.now() - new Date(lastSeenAt).getTime()) / 60000);
  if (mins <= 5) return { key: "online", label: "Online", color: "bg-emerald-500" };
  if (mins <= 30) return { key: "away", label: "Away", color: "bg-amber-400" };
  return { key: "offline", label: "Offline", color: "bg-zinc-400" };
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { me, refresh } = useAuth();
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bioEditing, setBioEditing] = useState(false);
  const [bioDraft, setBioDraft] = useState("");
  const [bioSaving, setBioSaving] = useState(false);

  const presence = deriveStatus(me?.last_seen_at || new Date().toISOString());

  useEffect(() => {
    let alive = true;
    api.get("/settings/me")
      .then((r) => { if (alive) { setSettings(r.data); setBioDraft(r.data?.bio || ""); } })
      .catch((e) => alive && setError(apiErrorMessage(e)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  async function persist(next, extras = {}) {
    return api.put("/settings/me", {
      username: next.username || null,
      email: next.email || null,
      avatar_url: next.avatar_url || null,
      bio: next.bio || null,
      banner_url: next.banner_url || null,
      preferences: next.preferences || {},
      ...extras,
    });
  }

  async function onPickAvatar(e) {
    const file = e.target.files?.[0]; if (!file) return;
    setAvatarUploading(true); setError(null);
    try {
      const uploaded = await uploadFile(file, "avatar");
      const next = { ...(settings || {}), avatar_url: uploaded.file_url };
      const saved = await persist(next);
      setSettings(saved.data); await refresh();
      toast({ title: "Profile photo updated" });
    } catch (e) {
      setError(apiErrorMessage(e));
      toast({ title: "Upload failed", description: apiErrorMessage(e), variant: "destructive" });
    } finally { setAvatarUploading(false); }
  }

  async function onPickBanner(e) {
    const file = e.target.files?.[0]; if (!file) return;
    setBannerUploading(true); setError(null);
    try {
      const uploaded = await uploadFile(file, "banner");
      const next = { ...(settings || {}), banner_url: uploaded.file_url };
      const saved = await persist(next);
      setSettings(saved.data); await refresh();
      toast({ title: "Banner updated" });
    } catch (e) {
      setError(apiErrorMessage(e));
      toast({ title: "Upload failed", description: apiErrorMessage(e), variant: "destructive" });
    } finally { setBannerUploading(false); }
  }

  async function saveBio() {
    setBioSaving(true);
    try {
      const next = { ...(settings || {}), bio: bioDraft.slice(0, 500) };
      const saved = await persist(next);
      setSettings(saved.data); setBioEditing(false);
      toast({ title: "Bio saved" });
    } catch (e) {
      toast({ title: "Couldn't save bio", description: apiErrorMessage(e), variant: "destructive" });
    } finally { setBioSaving(false); }
  }

  async function removeBanner() {
    try {
      const next = { ...(settings || {}), banner_url: null };
      const saved = await persist(next);
      setSettings(saved.data); await refresh();
    } catch (e) { toast({ title: "Couldn't remove banner", variant: "destructive" }); }
  }

  if (loading) {
    return (
      <div data-testid="profile-loading" className="flex h-[60vh] items-center justify-center text-zinc-500">
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }
  if (!settings) {
    return <div data-testid="profile-error" className="px-4 py-10 text-center text-sm text-rose-500">{error || "Couldn't load profile"}</div>;
  }

  const username = settings?.username || settings?.discord_username || "Unnamed";
  const avatar = settings?.avatar_url || `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(username)}&backgroundColor=c8ff00&textColor=000000`;
  const banner = settings?.banner_url;
  const roles = settings?.roles && settings.roles.length ? settings.roles : [settings?.role || "clipper"];
  const isPrivate = !!settings?.preferences?.privacy_mode;

  return (
    <div data-testid="profile-page" className="-mx-4 space-y-6 sm:-mx-6 lg:-mx-10">
      {/* Banner — animated gradient by default; image if uploaded */}
      <div className="group relative h-[180px] w-full overflow-hidden sm:h-[240px] lg:h-[280px]" data-testid="profile-banner">
        {banner ? (
          <img src={banner} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-[length:200%_200%] animate-[profileGrad_18s_ease_infinite]"
               style={{ backgroundImage: "linear-gradient(120deg, #0f172a 0%, #312e81 25%, #c8ff00 50%, #831843 75%, #0f172a 100%)" }}
          />
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
        {/* Banner upload control (top-right) */}
        <label className="absolute right-3 top-3 inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-black/55 px-3 py-1.5 text-[11.5px] font-semibold text-white opacity-0 backdrop-blur transition-opacity hover:bg-black/75 group-hover:opacity-100">
          <Camera size={12} /> {bannerUploading ? "Uploading…" : (banner ? "Change Banner" : "Upload Banner")}
          <input data-testid="profile-banner-input" type="file" accept="image/*,image/gif" className="hidden" onChange={onPickBanner} />
        </label>
        {banner ? (
          <button
            data-testid="profile-banner-remove"
            onClick={removeBanner}
            className="absolute right-3 top-12 inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-[10.5px] font-semibold text-rose-300 opacity-0 backdrop-blur transition-opacity hover:bg-black/75 group-hover:opacity-100"
          >
            <X size={11} /> Remove
          </button>
        ) : null}
      </div>

      <div className="-mt-16 px-4 sm:px-8 lg:px-12">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:gap-6">
          {/* Avatar with upload affordance + auto presence dot */}
          <div className="group/avatar relative w-fit">
            <img
              src={avatar}
              alt=""
              data-testid="profile-avatar"
              className="h-28 w-28 rounded-full border-[5px] border-black object-cover shadow-[0_8px_30px_-8px_rgba(0,0,0,0.55)] dark:border-[#0a0a0a] sm:h-32 sm:w-32"
            />
            <label className="absolute inset-0 flex cursor-pointer items-center justify-center rounded-full bg-black/45 text-white opacity-0 transition-opacity hover:opacity-100 group-hover/avatar:opacity-100">
              <Camera size={20} />
              <input data-testid="profile-avatar-input" type="file" accept="image/*" className="hidden" onChange={onPickAvatar} />
            </label>
            <span
              data-testid="profile-presence-dot"
              className={`absolute bottom-1.5 right-1.5 h-5 w-5 rounded-full border-4 border-black dark:border-[#0a0a0a] ${presence.color} animate-pulse`}
              title={presence.label}
            />
            {avatarUploading ? (
              <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/55 text-white">
                <Loader2 className="animate-spin" size={18} />
              </div>
            ) : null}
          </div>

          <div className="min-w-0 flex-1 pb-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 data-testid="profile-username" className="text-2xl font-black tracking-tight text-zinc-900 dark:text-white sm:text-3xl" style={{ fontFamily: '"Archivo Black", system-ui, sans-serif' }}>
                @{username}
              </h1>
              {(settings?.role && settings.role !== "clipper") ? (
                <BadgeCheck size={20} className="text-[#c8ff00]" />
              ) : null}
              <span data-testid="profile-presence-label" className="ml-1 inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10.5px] font-semibold text-zinc-600 dark:bg-white/[0.05] dark:text-zinc-300">
                <span className={`h-1.5 w-1.5 rounded-full ${presence.color} animate-pulse`} />
                {presence.label}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-1.5 text-[13.5px] text-zinc-600 dark:text-zinc-400">
              <Mail size={13} className="opacity-70" />
              <span data-testid="profile-email">{settings?.email || "—"}</span>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2 self-start sm:self-end">
            <button
              data-testid="profile-edit-btn"
              onClick={() => navigate("/settings")}
              className="inline-flex items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-4 py-2 text-[12.5px] font-semibold text-zinc-800 transition-colors hover:bg-zinc-50 dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-zinc-100 dark:hover:bg-white/[0.08]"
            >
              <Edit2 size={13} /> Edit Profile
            </button>
            <button
              data-testid="profile-settings-btn"
              onClick={() => navigate("/settings")}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-zinc-300 bg-white text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-zinc-100 dark:hover:bg-white/[0.08]"
              aria-label="Settings"
            >
              <SettingsIcon size={14} />
            </button>
          </div>
        </div>

        {/* Info cards */}
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          {/* Bio (inline editable) */}
          <div data-testid="profile-bio-card" className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-white/[0.08] dark:bg-white/[0.025] lg:col-span-2">
            <div className="flex items-center justify-between">
              <h3 className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Bio</h3>
              {!bioEditing ? (
                <button
                  data-testid="profile-bio-edit"
                  onClick={() => { setBioDraft(settings?.bio || ""); setBioEditing(true); }}
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-white/[0.05]"
                >
                  <Edit2 size={11} /> Edit
                </button>
              ) : null}
            </div>
            {!bioEditing ? (
              settings?.bio ? (
                <p className="mt-2 whitespace-pre-line text-[14px] leading-relaxed text-zinc-700 dark:text-zinc-300">{settings.bio}</p>
              ) : (
                <p className="mt-2 text-[13px] italic text-zinc-500 dark:text-zinc-500">No bio yet — click <span className="font-semibold">Edit</span> to add one.</p>
              )
            ) : (
              <div className="mt-2 space-y-2">
                <textarea
                  data-testid="profile-bio-input"
                  value={bioDraft}
                  onChange={(e) => setBioDraft(e.target.value.slice(0, 500))}
                  rows={4}
                  placeholder="Tell others about yourself…"
                  className="w-full resize-y rounded-xl border border-zinc-200 bg-white px-3 py-2 text-[13.5px] text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#c8ff00]/40 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-white"
                />
                <div className="flex items-center justify-between text-[11px] text-zinc-500">
                  <span>{bioDraft.length}/500</span>
                  <div className="flex gap-2">
                    <button
                      data-testid="profile-bio-cancel"
                      onClick={() => { setBioEditing(false); setBioDraft(settings?.bio || ""); }}
                      className="rounded-full px-3 py-1 text-[11.5px] font-semibold text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-white/[0.05]"
                    >
                      Cancel
                    </button>
                    <button
                      data-testid="profile-bio-save"
                      onClick={saveBio}
                      disabled={bioSaving}
                      className="inline-flex items-center gap-1 rounded-full bg-[#c8ff00] px-3 py-1 text-[11.5px] font-bold text-black hover:bg-[#b6ee00] disabled:opacity-60"
                    >
                      {bioSaving ? <Loader2 className="animate-spin" size={11} /> : <Check size={11} />}
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div data-testid="profile-roles-card" className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-white/[0.08] dark:bg-white/[0.025]">
            <h3 className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">Roles</h3>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {roles.map((r) => (
                <span key={r} data-testid={`profile-role-${r}`} className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-[11.5px] font-semibold capitalize text-zinc-700 dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-zinc-200">
                  {r === "admin" || r === "mod" ? <Crown size={11} className="text-amber-400" /> : null}
                  {r}
                </span>
              ))}
            </div>
            {isPrivate ? (
              <div className="mt-4 rounded-lg border border-amber-500/25 bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-700 dark:text-amber-300">
                Privacy mode is ON — your banner, picture and details are hidden from others.
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
